const SIZE_LABELS = ['size', 'taille', 'größe', 'tamanho', 'tamaño', 'koko', 'サイズ', 'taglia', 'size_id'];

const SELECT_ARROW_SVG = `
  <svg role="presentation" viewBox="0 0 19 12" class="Icon Icon--select-arrow">
    <polyline fill="none" stroke="currentColor" points="17 2 9.5 10 2 2" fill-rule="evenodd" stroke-width="2" stroke-linecap="square"></polyline>
  </svg>
`;

const CLOSE_ICON_SVG = `
  <svg role="presentation" viewBox="0 0 16 14" class="Icon Icon--select-arrow">
    <path d="M15 0L1 14m14 0L1 0" stroke="currentColor" fill="none" fill-rule="evenodd"></path>
  </svg>
`;

function getCartUrl() {
  return (window.routes && window.routes.cartUrl) || '/cart';
}

function getCart() {
  return fetch(`${getCartUrl()}.js`, {
    credentials: 'same-origin',
    method: 'GET',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
  }).then((response) => response.json());
}

function updateCart(body) {
  return fetch(`${getCartUrl()}/update.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  }).then((response) => response.json());
}

function addToCart(body) {
  return fetch(`${getCartUrl()}/add.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  }).then((response) => response.json());
}

function fetchProductByHandle(handle) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return fetch(`${root}products/${handle}.js`, {
    credentials: 'same-origin',
    method: 'GET',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Unable to load product ${handle}`);
    }
    return response.json();
  });
}

function parseJsonAttribute(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    try {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = value;
      return JSON.parse(textarea.value);
    } catch (nestedError) {
      console.error('[cart-size-selector] Invalid product JSON', nestedError);
      return null;
    }
  }
}

function getSizeOptionIndex(product) {
  if (!product || !Array.isArray(product.options)) return -1;

  return product.options.findIndex((option) => {
    const name = typeof option === 'string' ? option : option && option.name;
    return name && SIZE_LABELS.includes(String(name).toLowerCase());
  });
}

function getSizeValues(product, sizeOptionIndex) {
  if (sizeOptionIndex < 0 || !product || !Array.isArray(product.variants)) return [];

  const optionKey = `option${sizeOptionIndex + 1}`;
  const values = [];

  product.variants.forEach((variant) => {
    const value = variant[optionKey];
    if (value != null && value !== '' && !values.includes(value)) {
      values.push(value);
    }
  });

  return values;
}

function findVariantForSize(product, size, preferredVariant) {
  const sizeOptionIndex = getSizeOptionIndex(product);
  if (sizeOptionIndex < 0) return null;

  const optionKey = `option${sizeOptionIndex + 1}`;
  const baseOptions = preferredVariant
    ? [preferredVariant.option1, preferredVariant.option2, preferredVariant.option3]
    : [null, null, null];

  baseOptions[sizeOptionIndex] = size;

  const exactMatch = product.variants.find((variant) => {
    return (
      variant.option1 === baseOptions[0] &&
      (variant.option2 || null) === (baseOptions[1] || null) &&
      (variant.option3 || null) === (baseOptions[2] || null)
    );
  });

  if (exactMatch) return exactMatch;

  return product.variants.find((variant) => variant[optionKey] === size) || null;
}

function extractBasicSku(sku) {
  if (!sku) return null;
  const parts = String(sku).split('#');
  return parts[parts.length - 1] || null;
}

function remapPropertyValue(value, replacements) {
  if (typeof value !== 'string') return value;

  let nextValue = value;
  Object.keys(replacements).forEach((from) => {
    const to = replacements[from];
    if (!from || from === to) return;

    if (nextValue === from) {
      nextValue = to;
      return;
    }

    if (nextValue.indexOf(from) !== -1) {
      nextValue = nextValue.split(from).join(to);
    }
  });

  return nextValue;
}

function remapProperties(properties, replacements, extra = {}) {
  const nextProperties = Object.assign({}, properties || {}, extra);

  Object.keys(nextProperties).forEach((key) => {
    nextProperties[key] = remapPropertyValue(nextProperties[key], replacements);
  });

  return nextProperties;
}

function buildReplacementMap(pairs) {
  const replacements = {};
  pairs.forEach(([from, to]) => {
    if (from == null || to == null) return;
    const fromStr = String(from);
    const toStr = String(to);
    if (fromStr && fromStr !== toStr) {
      replacements[fromStr] = toStr;
    }
  });
  return replacements;
}

if (!customElements.get('cart-size-selector')) {
  customElements.define(
    'cart-size-selector',
    class CartSizeSelector extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.lineKey = this.getAttribute('data-line-key') || this.getAttribute('line-key');
        this.currentSize = this.getAttribute('data-current-size') || this.getAttribute('current-size') || '';
        this.sectionId =
          this.getAttribute('data-section-id') ||
          this.getAttribute('section-id') ||
          this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') ||
          '';
        this.product = parseJsonAttribute(this.querySelector('[data-product]')?.textContent);
        this.isUpdating = false;
        this.isOpen = false;
        this.popoverId = `cart-size-popover-${this.lineKey || this.product?.id || Date.now()}`.replace(
          /[^a-zA-Z0-9_-]/g,
          '-',
        );

        this.sizeOptionIndex = getSizeOptionIndex(this.product);
        this.sizeValues = getSizeValues(this.product, this.sizeOptionIndex);

        if (!this.product || !this.lineKey || this.sizeValues.length <= 1) {
          this.hidden = true;
          return;
        }

        if (!this.currentSize) {
          const currentVariant = this.product.variants?.find(
            (variant) => String(variant.id) === String(this.getAttribute('data-variant-id')),
          );
          const optionKey = `option${this.sizeOptionIndex + 1}`;
          this.currentSize = currentVariant?.[optionKey] || this.sizeValues[0];
        }

        this.render();
        this.bindEvents();
      }

      disconnectedCallback() {
        this.unbindEvents();
      }

      render() {
        const optionName =
          typeof this.product.options[this.sizeOptionIndex] === 'string'
            ? this.product.options[this.sizeOptionIndex]
            : this.product.options[this.sizeOptionIndex]?.name || 'Size';

        const valuesHtml = this.sizeValues
          .map((value) => {
            const isSelected = value === this.currentSize;
            const matchingVariant = findVariantForSize(this.product, value);
            const disabled = matchingVariant && matchingVariant.available === false;
            return `
              <button
                type="button"
                class="Popover__Value Heading Link Link--primary u-h6${isSelected ? ' is-selected' : ''}${disabled ? ' disabled' : ''}"
                data-value="${this.escapeHtml(value)}"
                data-option-position="${this.sizeOptionIndex + 1}"
                data-action="select-value"
                data-option-type="size"
                ${disabled ? 'disabled' : ''}
              >
                ${this.escapeHtml(value)}
              </button>
            `;
          })
          .join('');

        this.innerHTML = `
          <div class="ProductForm__Option CartSizeSelector">
            <button
              type="button"
              class="ProductForm__Item"
              aria-expanded="false"
              aria-controls="${this.popoverId}"
              data-option-type="size"
            >
              <span class="ProductForm__OptionName">
                <span class="ProductForm__SelectedValue">${this.escapeHtml(this.currentSize)}</span>
              </span>
              ${SELECT_ARROW_SVG}
            </button>
          </div>
          <div
            id="${this.popoverId}"
            class="OptionSelector OptionSelector--productItem Popover Popover--withMinWidth Popover--product_item Popover--cart"
            aria-hidden="true"
          >
            <header class="Popover__Header">
              <button type="button" class="Popover__Close Icon-Wrapper--clickable" data-action="close-popover">
                ${CLOSE_ICON_SVG}
              </button>
              <span class="Popover__Title Heading u-h4">${this.escapeHtml(optionName)}</span>
            </header>
            <div class="Popover__Content">
              <div class="Popover__ValueList" data-scrollable>
                ${valuesHtml}
              </div>
            </div>
          </div>
        `;

        this.activator = this.querySelector('.ProductForm__Item');
        this.popoverEl = this.querySelector(`#${this.popoverId}`);
        this.selectedValueEl = this.querySelector('.ProductForm__SelectedValue');
      }

      bindEvents() {
        this.onActivatorClick = this.toggle.bind(this);
        this.onPopoverClick = this.onPopoverClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);

        this.activator?.addEventListener('click', this.onActivatorClick);
        this.popoverEl?.addEventListener('click', this.onPopoverClick);
        document.addEventListener('click', this.onDocumentClick);
        this.popoverEl?.addEventListener('keyup', this.onKeyUp);
      }

      unbindEvents() {
        this.activator?.removeEventListener('click', this.onActivatorClick);
        this.popoverEl?.removeEventListener('click', this.onPopoverClick);
        document.removeEventListener('click', this.onDocumentClick);
        this.popoverEl?.removeEventListener('keyup', this.onKeyUp);
      }

      toggle(event) {
        event?.preventDefault();
        event?.stopPropagation();
        if (this.isUpdating) return;

        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      }

      open() {
        if (!this.popoverEl || !this.activator || this.isOpen || this.isUpdating) return;

        this.popoverEl.setAttribute('aria-hidden', 'false');
        this.activator.setAttribute('aria-expanded', 'true');
        this.positionPopover();
        this.isOpen = true;
      }

      close() {
        if (!this.popoverEl || !this.activator || !this.isOpen) return;

        this.popoverEl.setAttribute('aria-hidden', 'true');
        this.activator.setAttribute('aria-expanded', 'false');
        this.popoverEl.removeAttribute('style');
        this.isOpen = false;
      }

      positionPopover() {
        if (!this.popoverEl || !this.activator) return;

        const activatorRect = this.activator.getBoundingClientRect();
        const top = activatorRect.bottom + 8;

        this.popoverEl.style.position = 'fixed';
        this.popoverEl.style.zIndex = '1000';
        window.innerWidth >= 999 ? (this.popoverEl.style.top = `${top}px`) : null;
        this.popoverEl.classList.add('Popover--positionBottom', 'Popover--alignRight');
      }

      onDocumentClick(event) {
        if (!this.isOpen) return;
        if (this.contains(event.target) || this.popoverEl?.contains(event.target)) return;
        this.close();
      }

      onKeyUp(event) {
        if (event.key === 'Escape') {
          this.close();
        }
      }

      onPopoverClick(event) {
        const closeButton = event.target.closest('[data-action="close-popover"]');
        if (closeButton) {
          event.preventDefault();
          this.close();
          return;
        }

        const valueButton = event.target.closest('[data-action="select-value"]');
        if (!valueButton || valueButton.disabled || valueButton.classList.contains('disabled')) {
          return;
        }

        event.preventDefault();
        const newSize = valueButton.getAttribute('data-value');
        this.popoverEl
          ?.querySelectorAll('.Popover__Value.is-selected')
          .forEach((el) => el.classList.remove('is-selected'));
        valueButton.classList.add('is-selected');
        this.close();
        this.onSizeSelected(newSize);
      }

      async onSizeSelected(newSize) {
        if (!newSize || newSize === this.currentSize || this.isUpdating) return;

        this.setLoading(true);
        document.dispatchEvent(new CustomEvent('theme:loading:start'));

        try {
          await this.updateCartSize(newSize);
          this.currentSize = newSize;
          if (this.selectedValueEl) {
            this.selectedValueEl.textContent = newSize;
          }
          this.refreshCartSection();
        } catch (error) {
          console.error('[cart-size-selector] Failed to update size', error);
        } finally {
          this.setLoading(false);
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        }
      }

      setLoading(isLoading) {
        this.isUpdating = isLoading;
        this.classList.toggle('is-loading', isLoading);
        this.setAttribute('aria-busy', isLoading ? 'true' : 'false');

        if (this.activator) {
          this.activator.disabled = isLoading;
        }
      }

      async updateCartSize(newSize) {
        const cart = await getCart();
        const lineItem = cart.items.find((item) => item.key === this.lineKey);

        if (!lineItem) {
          throw new Error('Line item not found in cart');
        }

        const currentVariant =
          this.product.variants.find((variant) => variant.id === lineItem.variant_id) ||
          this.product.variants.find((variant) => variant[`option${this.sizeOptionIndex + 1}`] === this.currentSize);

        const newVariant = findVariantForSize(this.product, newSize, currentVariant);
        if (!newVariant) {
          throw new Error(`No variant found for size ${newSize}`);
        }

        if (newVariant.id === lineItem.variant_id) {
          return;
        }

        const groupId = lineItem.properties && lineItem.properties._id;
        const groupItems = groupId
          ? cart.items.filter((item) => item.properties && item.properties._id === groupId)
          : [lineItem];

        const updates = {};
        const plannedItems = [];

        for (const item of groupItems) {
          updates[item.key] = 0;

          if (item.key === this.lineKey) {
            plannedItems.push({
              sourceItem: item,
              id: newVariant.id,
              quantity: item.quantity,
              sku: newVariant.sku,
              isPrimary: true,
            });
            continue;
          }

          const relatedPlan = await this.planRelatedItem(item, newSize, newVariant);
          plannedItems.push(relatedPlan);
        }

        const replacements = buildReplacementMap(plannedItems.map((plan) => [plan.sourceItem.variant_id, plan.id]));

        if (groupId && String(groupId).indexOf(String(lineItem.variant_id) + ':') === 0) {
          replacements[String(groupId)] = String(groupId).replace(
            String(lineItem.variant_id) + ':',
            String(newVariant.id) + ':',
          );
        }

        const syncedItems = plannedItems.map((plan) => {
          const extra = {};

          if (plan.sourceItem.properties && plan.sourceItem.properties._basicSku && plan.sku) {
            extra._basicSku = plan.sku;
          }

          if (plan.sourceItem.properties && plan.sourceItem.properties._customization && plan.sku) {
            extra._sku = plan.sku;
          }

          return {
            id: plan.id,
            quantity: plan.quantity,
            properties: remapProperties(plan.sourceItem.properties, replacements, extra),
          };
        });

        const sharedGroupId =
          syncedItems.find((item) => item.properties && item.properties._id)?.properties?._id || null;

        if (sharedGroupId) {
          syncedItems.forEach((item) => {
            if (!item.properties) item.properties = {};
            item.properties._id = sharedGroupId;
          });
        }

        const allVariantIds = syncedItems.map((item) => String(item.id));
        syncedItems.forEach((item) => {
          if (!item.properties || item.properties._customizationProducts === undefined) return;
          item.properties._customizationProducts = allVariantIds.filter((id) => id !== String(item.id)).join(',');
        });

        syncedItems.forEach((item) => {
          if (!item.properties) return;
          Object.keys(item.properties).forEach((key) => {
            if (item.properties[key] === undefined) {
              delete item.properties[key];
            }
          });
        });

        const sectionPayload = this.getSectionPayload();
        await updateCart(Object.assign({ updates }, sectionPayload));
        await addToCart(Object.assign({ items: syncedItems }, sectionPayload));
      }

      async planRelatedItem(item, newSize, newMainVariant) {
        let nextVariantId = item.variant_id;
        let nextSku = item.sku;
        let relatedProduct = null;

        try {
          relatedProduct = await fetchProductByHandle(item.handle);
        } catch (error) {
          console.warn('[cart-size-selector] Unable to load related product', item.handle, error);
        }

        if (relatedProduct) {
          const relatedSizeIndex = getSizeOptionIndex(relatedProduct);
          const currentRelatedVariant = relatedProduct.variants.find((variant) => variant.id === item.variant_id);

          if (relatedSizeIndex >= 0) {
            const matchedVariant = findVariantForSize(relatedProduct, newSize, currentRelatedVariant);
            if (matchedVariant) {
              nextVariantId = matchedVariant.id;
              nextSku = matchedVariant.sku;
            }
          } else {
            const basicSku = extractBasicSku(newMainVariant.sku);
            if (basicSku) {
              const matchedNewSku = relatedProduct.variants.find(
                (variant) => variant.sku === basicSku || extractBasicSku(variant.sku) === basicSku,
              );

              if (matchedNewSku) {
                nextVariantId = matchedNewSku.id;
                nextSku = matchedNewSku.sku;
              }
            }
          }
        }

        return {
          sourceItem: item,
          id: nextVariantId,
          quantity: item.quantity,
          sku: nextSku,
          isPrimary: false,
        };
      }

      getSectionPayload() {
        if (!this.sectionId) return {};
        return {
          sections: this.sectionId,
          sections_url: window.location.pathname,
        };
      }

      refreshCartSection() {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
          }),
        );
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    },
  );
}
